import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';

interface ButtonProps {
  children: React.ReactNode;
  to?: string;
  href?: string;
  onClick?: () => void;
  type?: 'button' | 'submit' | 'reset';
  variant?: 'primary' | 'secondary' | 'outline' | 'outline-white' | 'ghost';
  size?: 'small' | 'medium' | 'large';
  className?: string;
  icon?: React.ReactNode;
  iconPosition?: 'left' | 'right';
}

const Button = ({
  children,
  to,
  href,
  onClick,
  type = 'button',
  variant = 'primary',
  size = 'medium',
  className = '',
  icon,
  iconPosition = 'right'
}: ButtonProps) => {
  // Button variations
  const variants: Record<NonNullable<ButtonProps['variant']>, string> = {
    primary: 'bg-primary hover:bg-primary-dark text-white font-medium',
    secondary: 'bg-secondary hover:bg-secondary-dark text-white font-medium',
    outline: 'bg-transparent border-2 border-primary text-primary hover:bg-primary hover:text-white',
    'outline-white': 'bg-transparent border-2 border-white text-white hover:bg-white hover:text-primary-dark',
    ghost: 'bg-transparent text-primary hover:bg-primary-light/20',
  };

  // Button sizes
  const sizes: Record<NonNullable<ButtonProps['size']>, string> = {
    small: 'py-1.5 px-3 text-sm',
    medium: 'py-2.5 px-5 text-base',
    large: 'py-3 px-6 text-lg',
  };

  // Common classes
  const baseClasses = 'inline-flex items-center justify-center rounded-md transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary/50';

  // Combined classes
  const buttonClasses = `${baseClasses} ${variants[variant]} ${sizes[size]} ${className}`;

  // Icon position classes
  const iconClasses = iconPosition === 'left' ? 'mr-2' : 'ml-2';

  // Render content with icon
  const content = (
    <>
      {icon && iconPosition === 'left' && <span className={iconClasses}>{icon}</span>}
      {children}
      {icon && iconPosition === 'right' && <span className={iconClasses}>{icon}</span>}
    </>
  );

  // Framer motion props
  const motionProps = {
    whileHover: { scale: 1.02 },
    whileTap: { scale: 0.98 },
    transition: { duration: 0.2 }
  };

  // Render as Link if 'to' prop is provided (internal link)
  if (to) {
    return (
      <motion.div {...motionProps} onClick={onClick}>
        <Link to={to} className={buttonClasses}>
          {content}
        </Link>
      </motion.div>
    );
  }

  // Render as anchor if 'href' prop is provided (external link)
  if (href) {
    return (
      <motion.div {...motionProps} onClick={onClick}>
        <a href={href} className={buttonClasses} target="_blank" rel="noopener noreferrer">
          {content}
        </a>
      </motion.div>
    );
  }

  // Otherwise render as button
  return (
    <motion.button
      type={type}
      onClick={onClick}
      className={buttonClasses}
      {...motionProps}
    >
      {content}
    </motion.button>
  );
};

export default Button;
